import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { SupabaseAuthCallback } from './components/SupabaseAuthCallback';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/SupabaseAuthContext';
import { AdminPage } from './pages/AdminPage';
import { AnalysesListPage } from './pages/AnalysesListPage';
import { AnalysisResultPage } from './pages/AnalysisResultPage';
import { DashboardPage } from './pages/DashboardPage';
import { PatientHistoryPage } from './pages/PatientHistoryPage';
import { PatientsPage } from './pages/PatientsPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { SupabaseLoginPage } from './pages/SupabaseLoginPage';
import { UploadPage } from './pages/UploadPage';
import './App.css';

const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#196a70] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slate-600 font-body">Loading your workspace...</p>
    </div>
  </div>
);

const NoWorkspace = () => {
  const { logout, user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-4">
        <img src="/pranagen-logo.png" alt="PranaGen Bio" className="h-12 mx-auto" />
        <h1 className="font-heading text-2xl font-bold text-slate-900">No workspace yet</h1>
        <p className="text-slate-600">
          {user?.email} isn't a member of a workspace yet. Ask your administrator to send you an invite.
        </p>
        <button onClick={logout} className="text-[#196a70] font-medium hover:underline">
          Sign out
        </button>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { loading, organization, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!organization) return <NoWorkspace />;
  return <Layout>{children}</Layout>;
};

// Blocks the read-only demo account from reaching write-only screens.
const RequireWritable = ({ children }) => {
  const { isDemo } = useAuth();
  if (isDemo) return <Navigate to="/dashboard" replace />;
  return children;
};

const LoginRoute = () => {
  const { loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <SupabaseLoginPage />;
};

// Shareable one-click demo entry: auto-signs into the shared demo workspace.
const DemoRoute = () => {
  const { loading, user, signInDemo } = useAuth();
  const [error, setError] = useState('');
  useEffect(() => {
    if (!loading && !user) {
      signInDemo().catch((e) => setError(e.message || 'The live demo is unavailable right now.'));
    }
  }, [loading, user, signInDemo]);
  if (user) return <Navigate to="/dashboard" replace />;
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <img src="/pranagen-logo.png" alt="PranaGen Bio" className="h-12 mx-auto" />
          <p className="text-red-600">{error}</p>
          <a href="/login" className="text-[#196a70] font-medium hover:underline">Back to sign in</a>
        </div>
      </div>
    );
  }
  return <LoadingScreen />;
};

const AppRouter = () => (
  <Routes>
    <Route path="/login" element={<LoginRoute />} />
    <Route path="/auth/callback" element={<SupabaseAuthCallback />} />
    <Route path="/set-password" element={<SetPasswordPage />} />
    <Route path="/demo" element={<DemoRoute />} />
    <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
    <Route path="/patients" element={<ProtectedRoute><PatientsPage /></ProtectedRoute>} />
    <Route path="/patients/:patientId/history" element={<ProtectedRoute><PatientHistoryPage /></ProtectedRoute>} />
    <Route path="/upload" element={<ProtectedRoute><RequireWritable><UploadPage /></RequireWritable></ProtectedRoute>} />
    <Route path="/analyses" element={<ProtectedRoute><AnalysesListPage /></ProtectedRoute>} />
    <Route path="/analyses/:analysisId" element={<ProtectedRoute><AnalysisResultPage /></ProtectedRoute>} />
    <Route path="/admin" element={<ProtectedRoute><RequireWritable><AdminPage /></RequireWritable></ProtectedRoute>} />
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);

export default function SupabaseApp() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
